/**
 * PostHog product analytics wrapper.
 * https://posthog.com/docs/libraries/js
 *
 * Mirrors the Amplitude wrapper (`utils/Analytics/amplitude.ts`): a thin
 * singleton initialised once at app boot. All calls no-op when no key is
 * configured, so local dev / CI without a key neither emit nor throw.
 */

import posthog from 'posthog-js';
import { CONFIG } from 'config/environment_variables';

const isPostHogConfigured = (): boolean => Boolean(CONFIG.POSTHOG_KEY);

// Initialize PostHog
export const initPostHog = () => {
  if (!isPostHogConfigured()) return;

  posthog.init(CONFIG.POSTHOG_KEY, {
    // Optional override. When unset, posthog-js defaults to US cloud
    // (https://us.i.posthog.com). Set it only for EU / self-hosted / proxied.
    ...(CONFIG.POSTHOG_HOST ? { api_host: CONFIG.POSTHOG_HOST } : {}),
    // Explicit, schema-defined events only — no auto pageviews/clicks (matches Amplitude).
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    // Only create person profiles for identified users (cost control).
    person_profiles: 'identified_only',
  });
};

// Associate subsequent events with a stable distinct id.
export const posthogIdentify = (id: string) => {
  if (!isPostHogConfigured()) return;
  posthog.identify(id);
};

// Track an event with optional properties.
export const posthogCapture = (
  event: string,
  properties?: Record<string, unknown>,
) => {
  if (!isPostHogConfigured()) return;
  posthog.capture(event, properties);
};
